import { createSetter } from 'rewire-common';
import { IFormContext, Form, arraySearch } from 'rewire-ui';
import DataMutations from '../graphql/Mutations';
import DataQueries from '../graphql/Queries';
import EditDialogModel, { IEditDialogModelState } from '../schedule/EditDialogModel';
import mainPageModel from '../main-navigation/MainPageModel';
import authService from '../services/AuthService';
import { ISecurityToken } from '../DataTypes';
import { freeze } from 'rewire-core';
import SettingsModel from './SettingsModel';

export interface IRolePermissionDialogModelState extends IEditDialogModelState {
  type: string;
  accessDisabled: boolean;
  viewTabs: any[];
}

const _workflowNames = ['Employee', 'Clerk', 'Manager', 'Admin', 'Super Admin'];
const _workflowCodes = ['employee', 'clerk', 'manager', 'admin', 'super_admin'];
const _defaultRoles = [
  // 'desktop_access',
  // 'mobile_access',
  'view_core',
  'edit_core',
  'view_time',
  'edit_time',
  'view_timeoff',
  'view_timeoff_mobile',
  'edit_timeoff',
  'view_session',
  'edit_timeoff_balances',
  'create_delete_timeoff',
];

const RoleSchema = `
  id
  code
  name
  workflow
  isSystem
  securityTokens
`;

export class RolePermissionDialogModel extends EditDialogModel<IRolePermissionDialogModelState> {
  static title = 'Role Permissions';
  private _isDuplicating = false;
  private _tokenMap: { [path: string]: ISecurityToken[] } | undefined;
  private _hiddenRoles: string[] = [];

  constructor() {
    super(
      RolePermissionDialogModel.title,
      async (ids: string[]) => DataQueries.getRolesByIds(RoleSchema, ids),
      DataMutations.insertRoles,
      DataMutations.updateRoles,
      DataMutations.deleteRoles,
      () => this._asObject(),
      () => mainPageModel.canAddRoles,
      () => mainPageModel.canEditRoles,
      () => mainPageModel.canDeleteRoles,
    );
    this._setVisible('Admin');
    this.disabled = false;
    this.state.viewTabs = [];
  }

  get viewTabs() {
    return this.state.viewTabs;
  }

  async buildForm() {
    if (!this._tokenMap) {
      const tokens = await authService.getSecurityTokens();
      this._tokenMap = tokens.reduce((prev, curr) => {
        if (curr.path === '«hidden»') {
          this._hiddenRoles.push(curr.token);
          return prev;
        }
        const tokens = prev[curr.path] ?? (prev[curr.path] = []);
        tokens.push(curr);
        return prev;
      }, {} as any);
      this.form.addFields((form: IFormContext) => {
        return tokens.reduce((prev, curr) => {
          if (curr.path === '«hidden»') return prev;
          prev[curr.token] = form
            .switch()
            .label(curr.description)
            .accessor((item: any) => item?.securityTokens?.[curr.token] ?? false, createSetter(['securityTokens', curr.token]));
          return prev;
        }, {} as any);
      });
      this.state.viewTabs.set(
        Object.keys(this._tokenMap ?? {}).map((t) => ({
          [t]: t,
          disabled: () => this.state.type === t,
          onClick: () => this.handleClick(t),
          content: t,
        })),
      );
    }

    for (const f of _defaultRoles) {
      const field = this.form.field[f];
      if (field) field.value = true;
    }
    this._setVisible(this.state.type);
  }

  public toObject() {
    return this._asObject();
  }

  public duplicateItem(dialog: SettingsModel) {
    const duplicate: any = this.toObject();
    duplicate.name = `copy of ${duplicate.name}`;
    duplicate.code = `copy_of_${duplicate.code}`;
    duplicate.isSystem = false;
    this._isDuplicating = true;
    dialog.onSelectedItem = (_selectedItem: any) => {
      requestAnimationFrame(() => {
        freeze(() => {
          for (const field of this.form.fields) {
            this.form.field[field.name].visible = false;
          }
        });

        freeze(() => {
          for (const field of this.form.fields) {
            this.form.field[field.name].value = (this.form as any)._getFieldValue(field, duplicate);
          }
          this._setVisible(this.state.type);
          this.form.value.isSystem = false;
        });
        dialog.onSelectedItem = undefined;
        this._isDuplicating = false;
      });
    };
    dialog.addItem();
  }

  public _setVisible(type: string) {
    this.state.type = type;
    if (!this._tokenMap) return;
    const disabled = () => this.form.value.isSystem || !this.canEdit;
    const fields = this.form.fields;
    for (const d of fields) {
      d.visible = this._tokenMap[type].some((t) => t.token === d.name);
      d.disabled = disabled;
    }

    this.form.field['name'].visible = true;
    this.form.field['code'].visible = true;
    this.form.field['workflow'].visible = true;
  }

  private get disabled() {
    return this.state.accessDisabled as boolean;
  }

  private set disabled(value: boolean) {
    this.state.accessDisabled = value;
  }

  protected getTitle() {
    const title = RolePermissionDialogModel.title;

    if (!this.visible) {
      return title;
    }

    const name = this.name;
    if (name) {
      return `${title} - ${name}`;
    }
    return `${title}`;
  }

  async load(id?: string, isModal: boolean = true) {
    if (this._isDuplicating) return;

    await super.load(id, isModal);
    this.buildForm();
  }

  protected _asObject() {
    const form = this.form;
    const obj = form.fields.reduce((prev: ObjectType, current) => {
      (form as any)._setFieldValue(current, prev, current.value);
      return prev;
    }, {});

    if (obj.securityTokens) {
      obj.securityTokens = Object.keys(obj.securityTokens).reduce((prev, curr) => {
        if (obj.securityTokens[curr]) prev[curr] = true;
        return prev;
      }, {} as any);
    }

    for (const hidden of this._hiddenRoles) {
      if (!obj.securityTokens[hidden]) {
        obj.securityTokens[hidden] = true;
      }
    }

    return obj;
  }

  protected _createForm(): Form {
    return Form.create((form: IFormContext) => ({
      ...this.fields(form),
    }));
  }

  protected fields(form: IFormContext) {
    return {
      code: form.string({ visible: true }).label('Code'),
      name: form.string({ visible: true }).label('Name'),
      workflow: form
        .select(arraySearch(_workflowCodes, (i) => _workflowNames[_workflowCodes.indexOf(i as string)]))
        .accessor(
          (item: any) => item.workflow,
          (item: any, value) => (item.workflow = value),
        )
        .label(`Role's Workflow`),
    };
  }

  public handleClick(type: string) {
    this._setVisible(type);
  }
}

const permissionDialogModel = new RolePermissionDialogModel();
export default permissionDialogModel;
